/**
 * components/client-detail/DossierExportCard.tsx
 *
 * Reusable export action card used in DossierActionSidebar.
 * Displays icon, title, subtitle, and an independent download button.
 * Tracks its own phase: idle / downloading / generating / success / error.
 * Shows inline progress bar during file downloads (optional).
 * On success shows a green check for ~2.5s before resetting to idle.
 *
 * Created Apr 2026: dossier builder refactored to single-screen 3-column layout.
 * Updated Apr 2026: entire card is now clickable (not just the icon button).
 * Updated Apr 2026: added error phase handling with red visual state.
 */

import { Download, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ExportPhase } from '../../hooks/useDossierBuilder';

interface DossierExportCardProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  phase: ExportPhase;
  onDownload: () => void;
  disabled: boolean;
  progress?: { done: number; total: number };
}

export default function DossierExportCard({
  icon,
  title,
  subtitle,
  phase,
  onDownload,
  disabled,
  progress,
}: DossierExportCardProps) {
  const isActive = phase === 'downloading' || phase === 'generating';
  const isSuccess = phase === 'success';
  const isError = phase === 'error';
  const isDisabled = disabled || isActive;

  const showProgressBar = phase === 'downloading' && progress && progress.total > 0;
  const progressPct = showProgressBar
    ? Math.round((progress!.done / progress!.total) * 100)
    : 0;

  return (
    <div
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? undefined : 0}
      onClick={isDisabled ? undefined : onDownload}
      onKeyDown={isDisabled ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDownload(); } }}
      className={`flex flex-col gap-2 p-3 rounded-xl border transition-all duration-200 ${
        isSuccess
          ? 'border-success-200 bg-success-50'
          : isError
          ? 'border-error-200 bg-error-50'
          : isDisabled
          ? 'border-neutral-100 bg-neutral-50 opacity-60 cursor-not-allowed'
          : 'border-neutral-200 bg-white hover:border-primary-200 hover:shadow-soft cursor-pointer'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
            isSuccess ? 'bg-success-100 text-success-600' : isError ? 'bg-error-100 text-error-600' : 'bg-primary-50 text-primary-600'
          }`}
        >
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium leading-tight ${
            isSuccess ? 'text-success-800' : isError ? 'text-error-800' : 'text-neutral-800'
          }`}>
            {title}
          </p>
          <p className={`text-xs mt-0.5 ${
            isSuccess ? 'text-success-600' : isError ? 'text-error-600' : 'text-neutral-400'
          }`}>
            {subtitle}
          </p>
        </div>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          disabled={isDisabled}
          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
            isSuccess
              ? 'bg-success-100 text-success-600'
              : isError
              ? 'bg-error-100 text-error-600 hover:bg-error-200 cursor-pointer'
              : isActive
              ? 'bg-primary-50 text-primary-400 cursor-not-allowed'
              : isDisabled
              ? 'bg-neutral-100 text-neutral-300 cursor-not-allowed'
              : 'bg-primary-50 text-primary-600 hover:bg-primary-100 hover:scale-105 active:scale-95 cursor-pointer'
          }`}
          aria-label={`Télécharger ${title}`}
        >
          {isActive ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSuccess ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : isError ? (
            <AlertCircle className="w-4 h-4" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </button>
      </div>

      {showProgressBar && (
        <div className="space-y-1">
          <div className="h-1 rounded-full bg-primary-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-right text-[10px] text-neutral-400">{progressPct}%</p>
        </div>
      )}

      {phase === 'downloading' && (!progress || progress.total === 0) && (
        <p className="text-[10px] text-primary-500 animate-pulse">Préparation…</p>
      )}
    </div>
  );
}
